import { useState } from 'react';

export default function ConsultationForm() {
  const [formData, setFormData] = useState({
    facilityType: '',
    facilityName: '',
    capacity: '',
    currentOccupancy: '',
    caregiverCount: '',
    helperCount: '',
    name: '',
    phone: '',
    email: '',
    currentInsurer: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('facilityType', formData.facilityType);
      formBody.append('facilityName', formData.facilityName);
      
      // 시설 유형에 따라 다른 필드 추가
      if (['노인요양기관', '노인요양공동생활가정', '단기보호기관', '주야간보호기관'].includes(formData.facilityType)) {
        formBody.append('capacity', formData.capacity);
        formBody.append('currentOccupancy', formData.currentOccupancy);
      } else if (['방문요양기관', '방문목욕기관'].includes(formData.facilityType)) {
        formBody.append('caregiverCount', formData.caregiverCount);
        formBody.append('helperCount', formData.helperCount);
      }
      
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('currentInsurer', formData.currentInsurer);
      formBody.append('message', formData.message);
      formBody.append('insuranceDocument', '수집 불가');

      const response = await fetch('https://readdy.ai/api/form/d533mq3ontabvievto1g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          facilityType: '',
          facilityName: '',
          capacity: '',
          currentOccupancy: '',
          caregiverCount: '',
          helperCount: '',
          name: '',
          phone: '',
          email: '',
          currentInsurer: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 시설 유형에 따라 표시할 필드 결정
  const showCapacityFields = ['노인요양기관', '노인요양공동생활가정', '단기보호기관', '주야간보호기관'].includes(formData.facilityType);
  const showCaregiverFields = ['방문요양기관', '방문목욕기관'].includes(formData.facilityType);

  return (
    <section id="consultation" className="py-20 bg-gradient-to-br from-[#8A2AE2]/5 to-[#5A2AE2]/5">
      <div className="max-w-3xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#8A2AE2]/10 text-[#5A2AE2] text-sm font-semibold rounded-full mb-4">
            CONSULTATION
          </span>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            무료 상담 신청
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            케어인슈 전문 컨설턴트가 시설에 맞는 최적의 보험 상품을 안내해 드립니다
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-10 border-2 border-[#8A2AE2]/20">
            <form id="consultation-form" onSubmit={handleSubmit} data-readdy-form>
              {/* 시설 유형 - 맨 앞으로 */}
              <div className="mb-6">
                <label htmlFor="facilityType" className="block text-sm font-semibold text-gray-700 mb-2">
                  시설 유형 <span className="text-red-500">*</span>
                </label>
                <select
                  id="facilityType"
                  name="facilityType"
                  value={formData.facilityType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8A2AE2] focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">선택하세요</option>
                  <option value="노인요양기관">노인요양기관</option>
                  <option value="노인요양공동생활가정">노인요양공동생활가정</option>
                  <option value="단기보호기관">단기보호기관</option>
                  <option value="주야간보호기관">주야간보호기관</option>
                  <option value="방문요양기관">방문요양기관</option>
                  <option value="방문목욕기관">방문목욕기관</option>
                  <option value="방문간호기관">방문간호기관</option>
                </select>
              </div>

              {/* 시설명 */}
              <div className="mb-6">
                <label htmlFor="facilityName" className="block text-sm font-semibold text-gray-700 mb-2">
                  시설명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="facilityName"
                  name="facilityName"
                  value={formData.facilityName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8A2AE2] focus:border-transparent text-sm"
                  placeholder="시설명을 입력하세요"
                />
              </div>

              {/* 정원/현원 (노인요양기관, 노인요양공동생활가정, 단기보호기관, 주야간보호기관) */}
              {showCapacityFields && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="capacity" className="block text-sm font-semibold text-gray-700 mb-2">
                      정원 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="capacity"
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleChange}
                      required
                      min="1"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8A2AE2] focus:border-transparent text-sm"
                      placeholder="정원을 입력하세요"
                    />
                  </div>
                  <div>
                    <label htmlFor="currentOccupancy" className="block text-sm font-semibold text-gray-700 mb-2">
                      현원 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="currentOccupancy"
                      name="currentOccupancy"
                      value={formData.currentOccupancy}
                      onChange={handleChange}
                      required
                      min="0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8A2AE2] focus:border-transparent text-sm"
                      placeholder="현원을 입력하세요"
                    />
                  </div>
                </div>
              )}

              {/* 요양보호사수/돌보미수 (방문요양기관, 방문목욕기관) */}
              {showCaregiverFields && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="caregiverCount" className="block text-sm font-semibold text-gray-700 mb-2">
                      요양보호사수 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="caregiverCount"
                      name="caregiverCount"
                      value={formData.caregiverCount}
                      onChange={handleChange}
                      required
                      min="0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8A2AE2] focus:border-transparent text-sm"
                      placeholder="요양보호사수를 입력하세요"
                    />
                  </div>
                  <div>
                    <label htmlFor="helperCount" className="block text-sm font-semibold text-gray-700 mb-2">
                      돌보미수 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="helperCount"
                      name="helperCount"
                      value={formData.helperCount}
                      onChange={handleChange}
                      required
                      min="0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8A2AE2] focus:border-transparent text-sm"
                      placeholder="돌보미수를 입력하세요"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8A2AE2] focus:border-transparent text-sm"
                    placeholder="이름을 입력하세요"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    연락처 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8A2AE2] focus:border-transparent text-sm"
                    placeholder="010-0000-0000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    이메일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8A2AE2] focus:border-transparent text-sm"
                    placeholder="example@email.com"
                  />
                </div>
                <div>
                  <label htmlFor="currentInsurer" className="block text-sm font-semibold text-gray-700 mb-2">
                    현재 가입 보험사
                  </label>
                  <input
                    type="text"
                    id="currentInsurer"
                    name="currentInsurer"
                    value={formData.currentInsurer}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8A2AE2] focus:border-transparent text-sm"
                    placeholder="현재 가입 보험사를 입력하세요"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  문의 내용
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8A2AE2] focus:border-transparent text-sm resize-none"
                  placeholder="상담받고 싶은 내용을 자유롭게 작성해 주세요 (최대 500자)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">
                  {formData.message.length} / 500자
                </p>
              </div>

              {/* 파일 첨부 */}
              <div className="mb-6">
                <label htmlFor="insuranceDocument" className="block text-sm font-semibold text-gray-700 mb-2">
                  현재 보험 증권 사본을 첨부하면 더 자세한 상담을 받으실 수 있습니다.(선택)
                </label>
                <div className="relative">
                  <input
                    type="file"
                    id="insuranceDocument"
                    name="insuranceDocument"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8A2AE2] focus:border-transparent text-sm cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#8A2AE2]/10 file:text-[#5A2AE2] hover:file:bg-[#8A2AE2]/20 file:cursor-pointer"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  PDF, JPG, PNG 파일만 업로드 가능합니다
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-[#8A2AE2] to-[#5A2AE2] text-white text-base font-semibold rounded-lg hover:from-[#7A1AD2] hover:to-[#4A1AD2] transition-all shadow-lg hover:shadow-xl disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? '전송 중...' : '상담 신청하기'}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-6 p-4 bg-[#8A2AE2]/10 border border-[#8A2AE2]/30 rounded-lg">
                  <p>
                    상담 신청이 완료되었습니다. 빠른 시일 내에 연락드리겠습니다.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p>
                    전송 중 오류가 발생했습니다. 다시 시도해 주세요.
                  </p>
                </div>
              )}
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md text-center border-2 border-[#8A2AE2]/20">
              <div className="w-12 h-12 bg-[#8A2AE2]/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <i className="ri-phone-line text-2xl text-[#5A2AE2]"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">전화 상담</h4>
              <p>평일 09:00 - 18:00</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md text-center border-2 border-[#8A2AE2]/20">
              <div className="w-12 h-12 bg-[#8A2AE2]/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <i className="ri-mail-line text-2xl text-[#5A2AE2]"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">이메일 상담</h4>
              <p>24시간 접수 가능</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md text-center border-2 border-[#8A2AE2]/20">
              <div className="w-12 h-12 bg-[#8A2AE2]/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <i className="ri-time-line text-2xl text-[#5A2AE2]"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">빠른 응답</h4>
              <p>24시간 내 답변</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
